import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection } from "@/components/FaqSection";
import { SITE_URL, hreflangLinks, faqPageSchema } from "@/lib/seo";

const PATH = "/india/gold-forecast";
const TITLE = "Kya Sone Ka Bhav Kam Hoga? — Gold Price Forecast India Live Analysis";
const DESC = "Kya sone ka bhav kam hoga aane wale dino mein? Live India gold price forecast, USD/INR impact, Fed rate outlook, wedding & festival demand analysis. Aaj ka trend read.";

const FAQS = [
  { q: "Kya sone ka bhav kam hoga India mein?", a: "Sone ka bhav kam hone ke liye do cheezein zaroori hain — international XAU/USD spot ka girna AUR USD/INR ka stable rehna. Agar rupee kamzor ho, to spot girne ke baawajood India retail rate flat ya oopar rah sakta hai. Short-term forecasts sirf direction batate hain, exact price nahi." },
  { q: "Will gold price decrease in coming days in India?", a: "Historically, gold has monthly moves of ±5–7%. Whether India retail rates fall depends on international spot movement plus USD/INR direction plus import duty adjustments." },
  { q: "Sone ka bhav kab girega?", a: "Gold typically corrects after big rallies (2020 August peak, 2022 March war spike). Triggers include US Fed rate hikes, dollar strengthening, ya global risk-on sentiment (equity rally)." },
  { q: "Why is gold price rising in India?", a: "Three reasons: (1) international safe-haven demand, (2) rupee depreciation against USD, (3) wedding + festival season demand (Diwali, Akshaya Tritiya, Dhanteras). Long-term, Indian household gold demand puts a floor under prices." },
  { q: "Should I buy gold now or wait in India?", a: "For jewellery, buy jab zaroorat ho — market timing usually costs more than it saves. For investment (SGB, gold ETF), monthly SIP (dollar-cost-average) beats lump-sum timing over 3+ year horizons." },
  { q: "How accurate are gold price predictions?", a: "Institutional forecasts (Goldman Sachs, JP Morgan) have historically missed year-end gold targets by 10–20%. Direction is somewhat predictable; exact price is not. Trust the trend, not the number." },
];

export const Route = createFileRoute("/india/gold-forecast")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "en_IN" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqPageSchema(FAQS)) }],
  }),
  component: IndiaForecastPage,
});

function IndiaForecastPage() {
  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/india" className="hover:text-primary">India Gold Rate</Link> / Forecast
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold">Kya Sone Ka Bhav Kam Hoga? — India Gold Price Forecast &amp; Trend</h1>
      <p className="mt-4 text-foreground/85 leading-relaxed">
        Aaj sone ka bhav India mein kis taraf ja raha hai? Ye page track karta hai
        teen main drivers — <strong>international XAU/USD spot</strong>,
        <strong> USD/INR exchange rate</strong>, aur India ki
        <strong> wedding + festival demand</strong>. Live per-10-gram figure ke liye,
        jump to <Link to="/india" className="text-primary underline">aaj ka sone ka bhav India</Link>.
      </p>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-bold mb-3">Sone ka bhav kab girega — 3 signals to watch</h2>
        <ol className="list-decimal ml-6 space-y-3 text-sm leading-relaxed">
          <li><strong>US Fed rate hikes</strong> → strong dollar → gold spot falls.</li>
          <li><strong>Rupee strengthening against USD</strong> → India retail rate falls even if global gold is flat.</li>
          <li><strong>Post-festival demand dip</strong> (Feb–May non-wedding months) → local premium compresses.</li>
        </ol>
      </section>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Link to="/india" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Aaj ka sone ka bhav India (live)</div>
          <div className="text-xs text-muted-foreground mt-1">24K, 22K, 18K per 10 gram — refreshed every 10 min.</div>
        </Link>
        <Link to="/aaj-ka-sone-ka-bhav" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Aaj ka sone ka bhav — full landing</div>
          <div className="text-xs text-muted-foreground mt-1">All-India rate by city, karat, weight.</div>
        </Link>
        <Link to="/sone-ka-bhav-22-carat" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">22 carat sone ka bhav aaj ka</div>
          <div className="text-xs text-muted-foreground mt-1">Jewellery-grade 916 gold rate.</div>
        </Link>
        <Link to="/silver-rate-india" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
          <div className="font-semibold">Chandi aur sone ka bhav</div>
          <div className="text-xs text-muted-foreground mt-1">Gold-silver ratio & live chandi rate.</div>
        </Link>
      </div>

      <FaqSection faqs={FAQS} title="India gold forecast — most-asked questions" />
    </main>
  );
}